import { format } from "date-fns";
import { PlusIcon } from "lucide-react";
import { Link, Outlet } from "react-router-dom";

import { useFilterControl } from "@/components/filter-control";
import { SearchBar } from "@/components/search-bar";
import { useTableControl } from "@/components/table-control";
import { Button } from "@/components/ui/button";
import { TabNavigation } from "@/components/ui/tab-navigation";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { PendingSessionsCounter } from "@/features/sessions/components/pending-sessions-counter";
import { RoleEnum, RoleGroupEnum } from "@classview/shared-types";

export type SessionsPageFilters = {
  date: string;
  activeMonth: string;
};

export default function SessionsPage() {
  const tableControl = useTableControl({ persistInUrl: false });
  const tableFilters = useFilterControl<SessionsPageFilters>({
    date: format(new Date(), "yyyy-MM-dd"),
    activeMonth: format(new Date(), "yyyy-MM"),
  });
  const { hasPermission, organisation } = useOrganisation();

  const isSessionGuest = organisation.roleGroup === RoleGroupEnum.SESSION_GUEST;

  return (
    <div>
      <OnboardingHeader
        title="Sessions"
        description="View your upcoming sessions"
        className="mb-6"
      />
      <div className="mb-2 flex items-center justify-between gap-2">
        <SearchBar
          search={tableControl.search}
          setSearch={tableControl.setSearch}
          className="mb-2 sm:mb-0 sm:max-w-xs"
        />

        {hasPermission(RoleEnum.CALL_MANAGEMENT) && (
          <Button asChild>
            <Link
              to={`/sessions/schedule?date=${format(tableFilters.filters.date, "yyyy-MM-dd")}`}
            >
              <PlusIcon className="mr-1 size-4 text-inherit" /> Schedule Session
            </Link>
          </Button>
        )}
      </div>

      {!isSessionGuest && (
        <TabNavigation
          className="my-6 md:my-10"
          options={[
            { label: "My Sessions", to: "/sessions/my-sessions" },
            { label: "All Sessions", to: "/sessions/all" },
            ...(hasPermission(RoleEnum.CALL_MANAGEMENT)
              ? [
                  {
                    label: "Room Systems",
                    to: "/sessions/room-systems",
                  },
                ]
              : []),
            ...(hasPermission(RoleEnum.CALL_MANAGEMENT) ||
            hasPermission(RoleEnum.ORGANISATION_MANAGEMENT)
              ? [
                  {
                    label: (
                      <span className="flex items-center gap-2">
                        Pending Invitations <PendingSessionsCounter />
                      </span>
                    ),
                    to: "/sessions/pending-invitations",
                  },
                ]
              : []),
          ]}
        />
      )}
      <Outlet context={{ tableControl, tableFilters }} />
    </div>
  );
}
